import { Grid, Stack } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useKandidaatMutaties } from '../../api/kandidaten.ts';
import type {
  Functietitel,
  Kandidaat,
  KandidaatProfiel,
  Kandidaatstatus,
  Locatie,
  Rijbewijs,
  Talentpool,
} from '../../api/typen.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { FunctietitelsVeld, LocatieVeld, TalentpoolsVeld } from '../../componenten/keuzevelden.tsx';
import { Keuzeveld, Meerkeuzeveld, Tekstveld } from '../../componenten/velden.tsx';
import { leegNaarNull } from '../../helpers/formulier.ts';
import { kandidaatstatusLabels, opties, rijbewijsLabels } from '../../helpers/labels.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

interface Waarden {
  voornaam: string;
  achternaam: string;
  geboortedatum: string;
  email: string;
  telefoon: string;
  linkedinUrl: string;
  straatHuisnummer: string;
  locatie: Locatie | null;
  rijbewijzen: Rijbewijs[];
  status: Kandidaatstatus;
  bron: string;
  functietitels: Functietitel[];
  talentpools: Talentpool[];
}

interface Props {
  open: boolean;
  onSluit: () => void;
  bestaand?: KandidaatProfiel | undefined;
  onOpgeslagen?: ((kandidaat: Kandidaat) => void) | undefined;
}

const statusOpties = opties(kandidaatstatusLabels);
const rijbewijsOpties = opties(rijbewijsLabels);

function beginwaarden(bestaand: KandidaatProfiel | undefined): Waarden {
  return {
    voornaam: bestaand?.voornaam ?? '',
    achternaam: bestaand?.achternaam ?? '',
    geboortedatum: bestaand?.geboortedatum ?? '',
    email: bestaand?.email ?? '',
    telefoon: bestaand?.telefoon ?? '',
    linkedinUrl: bestaand?.linkedinUrl ?? '',
    straatHuisnummer: bestaand?.straatHuisnummer ?? '',
    locatie: bestaand?.locatie ?? null,
    rijbewijzen: bestaand?.rijbewijzen ?? [],
    status: bestaand?.status ?? 'nieuw',
    bron: bestaand?.bron ?? '',
    functietitels: bestaand?.functietitels.map((koppeling) => koppeling.functietitel) ?? [],
    talentpools: bestaand?.talentpools.map((koppeling) => koppeling.talentpool) ?? [],
  };
}

/**
 * Registreert een kandidaat of wijzigt zijn gegevens. Bij een nieuwe kandidaat kan meteen de indeling in
 * functietitels en talentpools mee.
 * @param props De bestaande kandidaat om te wijzigen, of niets voor een nieuwe.
 * @returns De dialoog.
 */
export function KandidaatFormulier({ open, onSluit, bestaand, onOpgeslagen }: Props) {
  const mutaties = useKandidaatMutaties();
  const formulier = useForm<Waarden>({ defaultValues: beginwaarden(bestaand) });
  useEffect(() => {
    if (open) {
      formulier.reset(beginwaarden(bestaand));
    }
  }, [open, bestaand, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    async (waarden) => {
      const gegevens = {
        voornaam: waarden.voornaam,
        achternaam: waarden.achternaam,
        geboortedatum: leegNaarNull(waarden.geboortedatum),
        email: leegNaarNull(waarden.email),
        telefoon: leegNaarNull(waarden.telefoon),
        linkedinUrl: leegNaarNull(waarden.linkedinUrl),
        straatHuisnummer: leegNaarNull(waarden.straatHuisnummer),
        locatieId: waarden.locatie?.id ?? null,
        rijbewijzen: waarden.rijbewijzen,
        status: waarden.status,
        bron: leegNaarNull(waarden.bron),
      };
      const kandidaat = bestaand
        ? await mutaties.wijzig.mutateAsync({ id: bestaand.id, invoer: gegevens })
        : await mutaties.registreer.mutateAsync({
            ...gegevens,
            functietitelIds: waarden.functietitels.map((item) => item.id),
            talentpoolIds: waarden.talentpools.map((item) => item.id),
          });
      onOpgeslagen?.(kandidaat);
    },
    { melding: bestaand ? 'Kandidaat gewijzigd' : 'Kandidaat geregistreerd', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Kandidaat wijzigen' : 'Nieuwe kandidaat'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="md"
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="voornaam" label="Voornaam" verplicht autoFocus />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="achternaam" label="Achternaam" verplicht />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="email" label="E-mailadres" type="email" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="telefoon" label="Telefoon" type="tel" placeholder="06-12345678" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="geboortedatum" label="Geboortedatum" type="date" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld
              naam="linkedinUrl"
              label="LinkedIn-profiel"
              type="url"
              placeholder="https://www.linkedin.com/in/…"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="straatHuisnummer" label="Straat en huisnummer" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <LocatieVeld naam="locatie" label="Woonplaats" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Meerkeuzeveld naam="rijbewijzen" label="Rijbewijzen" opties={rijbewijsOpties} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Keuzeveld naam="status" label="Status" opties={statusOpties} verplicht />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Tekstveld naam="bron" label="Bron" placeholder="Bijv. website, LinkedIn" />
          </Grid>
          {!bestaand && (
            <Grid size={12}>
              <Stack spacing={2}>
                <FunctietitelsVeld naam="functietitels" label="Ingedeeld als functietitels" />
                <TalentpoolsVeld naam="talentpools" label="Talentpools" />
              </Stack>
            </Grid>
          )}
        </Grid>
      </FormulierDialoog>
    </FormProvider>
  );
}
